// The mode config (§31.1 Q2, Q9; §31 G1, G3): one row per front-door mode,
// and one per kind of run, in one place.
//
// **A table of the shell's modes, never read by the rules** — undo is a log
// operation and a turn timer is the shell's clock, so neither may reach
// `src/engine/rules/`. What a run's rules need (the spawn dial, the map) is
// *written into its opening command* by `runOpening`, so it replays and
// hashes like any other setup. Mode differences live here, never in a flag.

import type { GameMode } from "src/api/intents";
import type { ComposedLevel } from "src/engine/campaign/fragments";
import { KING, pieceColor, pieceType } from "src/engine/rules/board";
import type { LevelSetup } from "src/engine/rules/campaign/level";
import type { Command } from "src/engine/rules/commands";
import type { BoardMapId } from "src/engine/rules/maps";
import { COAL } from "src/engine/rules/materials";
import type { Ruleset, RulesetChoice } from "src/engine/rules/ruleset";
import { FUEL, METAL, STONE, STORE, type MaterialLine } from "src/shared/industry";

export interface ModeConfig {
  /** May the player take back actions this turn? False refuses Undo with a reason. */
  undo: boolean;
  /**
   * Does Undo reach past End Turn (§31 G1, Training's "unlimited undos")?
   * With nothing left to take back this turn, it hands back the previous
   * round — the Time Fluxxer's walk, free. Bounded by `new-game`.
   */
  undoPast: boolean;
  /** Seconds a turn may last, or null for no timer. Nothing reads it yet (Q9). */
  turnSeconds: number | null;
}

const ROW: ModeConfig = { undo: true, undoPast: false, turnSeconds: null };

export const MODE_CONFIG: Readonly<Record<GameMode, ModeConfig>> = {
  local: ROW,
  net: ROW,
  solo: ROW,
  run: ROW,
  training: { ...ROW, undoPast: true },
  continue: ROW,
  watch: ROW,
  "watch-run": ROW
};

/** The row for a mode; an unnamed mode is the default row. */
export const modeConfig = (mode: GameMode | undefined): ModeConfig =>
  mode === undefined ? ROW : MODE_CONFIG[mode];

/** Training's authored map (`public/levels/training.tmj`). */
export const TRAINING_LEVEL = "training";

/**
 * Normal Mode's board, composed from fragments (`campaign/fragments.ts`): the
 * player's opener at the origin and the first camp a board length north.
 * Replaced the mirrored Frontier map and its full enemy army — the design
 * asks for neither.
 */
export const NORMAL_LEVEL: ComposedLevel = {
  levelId: "normal",
  levelName: "Normal Mode",
  playerSide: 0,
  fragments: [
    { fragment: "normal-opener", file: 0, rank: 0 },
    { fragment: "first-camp", file: 0, rank: 8 }
  ]
};

/** The kinds of run the front door opens (§31 G1, G3). */
export type RunKind = "normal" | "training";

export interface RunConfig {
  /** What the door and the save call it. */
  name: string;
  /** The `MODE_CONFIG` row the run plays under. */
  mode: GameMode;
  /** A generated map the run opens on (§31 D3), for a run with no level. */
  map?: BoardMapId;
  /** The authored level (`campaign/levels.ts` id) the run opens on. */
  level?: string;
  /** Or a level composed from fragments. Either way the caller loads it. */
  compose?: ComposedLevel;
  /** How camps arrive — written into the opening's `ruleset`. */
  spawn: Ruleset["spawn"];
  /**
   * Material the player starts with, as `[grade, count, line]` (§31 G1's
   * "starting stockpile"): in the player's store in the opening rooms, else
   * the king's hands (§33 I1 — a piece holds 20, a store 50 or more).
   */
  stockpile: readonly (readonly [grade: number, count: number, line: MaterialLine])[];
}

export const RUN_CONFIG: Readonly<Record<RunKind, RunConfig>> = {
  // **Normal Mode** (§31 G3): the opener and a small first camp, composed
  // from fragments; more factions on the spawn clock, counted in rounds.
  normal: { name: "Normal Mode", mode: "run", compose: NORMAL_LEVEL, spawn: "clock", stockpile: [] },
  // **Training Mode** (§31 G1): an authored map with its camps drawn in,
  // unlimited undo, and a stockpile to build with.
  training: {
    name: "Training Mode",
    mode: "training",
    level: TRAINING_LEVEL,
    spawn: "defeat",
    // Coal first: a store hands on ties in stock order, and iron the king
    // cannot burn for is iron that sits (§33 I1).
    stockpile: [
      [COAL, 8, FUEL],
      [0, 12, METAL],
      [0, 12, STONE]
    ]
  }
};

/** Which kind of run a mode opens, or undefined for a mode that opens none. */
export const runKindOf = (mode: GameMode | undefined): RunKind | undefined =>
  mode === "training" ? "training" : mode === "run" ? "normal" : undefined;

/** Which kind a run in hand is — Training by its level, anything else Normal. */
export const runKindOfState = (state: { run?: { level?: { levelId: string } } }): RunKind =>
  state.run?.level?.levelId === TRAINING_LEVEL ? "training" : "normal";

export interface RunOpeningOptions {
  seed: number;
  /** The run's constitution (§26 F13). */
  hard?: boolean;
  /** The parsed level, for a kind that names one. */
  level?: LevelSetup;
}

/**
 * The `new-game` that opens a run of this kind.
 *
 * **The row decides, the command carries.** Spawn dial, map and stockpile go
 * into the command, so a replay of the log derives the same run without
 * this table — and the rules never see the table.
 */
export function runOpening(
  kind: RunKind,
  { seed, hard, level }: RunOpeningOptions
): Extract<Command, { type: "new-game" }> {
  const row = RUN_CONFIG[kind];
  if ((row.level !== undefined || row.compose) && !level) throw new Error(`${row.name} needs its level loaded`);
  const ruleset: RulesetChoice | undefined = row.spawn === "defeat" ? undefined : { spawn: row.spawn };
  const holder = level ? openingHolder(level) : -1;
  return {
    type: "new-game",
    seed,
    run: [],
    ...(row.map ? { map: row.map } : {}),
    ...(level ? { level } : {}),
    ...(ruleset ? { ruleset } : {}),
    ...(row.stockpile.length > 0 && holder >= 0
      ? {
          stock: row.stockpile.map(
            ([grade, count, line]): [number, number, number, MaterialLine] => [holder, grade, count, line]
          )
        }
      : {}),
    ...(hard ? { mode: "hard" as const } : {})
  };
}

/** Where the stockpile goes: the player's store in the opening rooms, else its king, else -1. */
function openingHolder(level: LevelSetup): number {
  const opening = level.regions.filter((region) => region.revealedBy === "");
  for (const region of opening) {
    for (const [sq, kind, owner] of region.structures ?? []) {
      if (kind === STORE && owner === level.playerSide) return sq;
    }
  }
  for (const region of opening) {
    for (const [sq, piece] of region.pieces) {
      if (pieceType(piece) === KING && pieceColor(piece) === level.playerSide) return sq;
    }
  }
  return -1;
}
